import { useSearchParams } from "react-router";

import { useNotification } from "@refinedev/core";
import moment from "moment";
import { create } from "zustand/react";

import { useEditCreated } from "@/components/common/enrichment-async-hint";
import type { TinaHitlSource } from "@/components/forms/load-from-clipboard/clipboard-data";
import { defineStepper } from "@/components/ui/stepper";
import { useLoadingOverlay } from "@/contexts/loading-context";
import { convertToTimezone, replaceTimezone } from "@/lib/dateUtils";
import { toHumanErrorMessage } from "@/lib/extensions/httpErrorExtension";
import { Sentry } from "@/lib/sentry";
import { useEventUpdates } from "@/pages/events/features/notifications/event-updates-context";
import { useEventRepository } from "@/pages/events/hooks/use-event-repository";
import {
  EventBasicInfoType,
  EventDraftType,
  EventMediaType,
  EventTicketingType,
} from "@/pages/events/schemas/event.create.form";
import { useItemsRepository } from "@/pages/tina/items/hooks/use-items-repository";
import type { ResolveHitlVariables } from "@/pages/tina/items/hooks/use-items-repository";

export const { Stepper, useStepper } = defineStepper(
  {
    id: "details",
    title: "Event Details",
    description: "Basic information about the event",
  },
  {
    id: "ticketing",
    title: "Ticketing",
    description: "Manage event ticketing and sales",
  },
  {
    id: "media",
    title: "Media & Shit",
    description: "Upload media, tags and other stuff",
  },
);

type State = {
  id?: string;
  basicInfoStep?: EventBasicInfoType;
  ticketingStep?: EventTicketingType;
  mediaStep?: EventMediaType;
  tinaHitlSource?: TinaHitlSource;
  loading: Record<
    "basicInfo" | "ticketing" | "media",
    "idle" | "success" | "loading" | "error"
  >;
};

type Action = {
  setId: (id: string) => void;
  updateBasicInfo: (info: EventBasicInfoType) => void;
  updateTicketing: (ticketing: EventTicketingType) => void;
  updateMedia: (media: EventMediaType) => void;
  updateTinaHitlSource: (source: TinaHitlSource | undefined) => void;
  clearTinaHitlSource: () => void;
  updateState: (state: Partial<State["loading"]>) => void;
};

export function buildTinaHitlAutoCloseRequest(
  source: TinaHitlSource,
  moodEventId: string,
): { eventId: string; values: ResolveHitlVariables } {
  return {
    eventId: source.tinaItemId,
    values: {
      entity_id: moodEventId,
      resolution_kind: "manual_event_created",
    },
  };
}

export const useEventCreateFormStore = create<State & Action>((set) => ({
  mediaStep: {
    id: "",
    hide: false,
    media: {
      photos: [],
      teasers: [],
    },
    tags: [],
  },
  setId: (id) =>
    set(() => ({
      id,
      mediaStep: {
        id,
        hide: false,
        media: { photos: [], teasers: [] },
        tags: [],
      },
    })),
  updateBasicInfo: (info) =>
    set(() => ({
      basicInfoStep: info,
    })),
  updateTicketing: (ticketing) => {
    set(() => ({
      ticketingStep: ticketing,
    }));
  },
  updateMedia: (media) => {
    set(() => ({
      mediaStep: media,
    }));
  },
  updateTinaHitlSource: (source) => {
    set(() => ({
      tinaHitlSource: source,
    }));
  },
  clearTinaHitlSource: () => {
    set(() => ({
      tinaHitlSource: undefined,
    }));
  },
  loading: {
    basicInfo: "idle",
    ticketing: "idle",
    media: "idle",
  },
  updateState: (loading) =>
    set((prev) => ({
      loading: {
        ...prev.loading,
        ...loading,
      },
    })),
}));

export const useEventCreateForm = () => {
  const methods = useStepper();
  const {
    setId,
    updateBasicInfo,
    updateTicketing,
    updateMedia,
    updateTinaHitlSource,
    clearTinaHitlSource,
    updateState,
    id,
    basicInfoStep,
    ticketingStep,
    mediaStep,
    tinaHitlSource,
    loading,
  } = useEventCreateFormStore();
  const { draft, complete, getDraft } = useEventRepository();
  const { resolveHitl } = useItemsRepository();
  const { setLoading } = useLoadingOverlay();
  const { open } = useNotification();
  const editCreated = useEditCreated();
  const [_, setSearchParams] = useSearchParams();

  const { setPendingUpdate, openConfirmationModal } = useEventUpdates();

  async function closeTinaHitlForCreatedEvent(moodEventId: string) {
    if (!tinaHitlSource?.autoCloseHitl) return;

    const request = buildTinaHitlAutoCloseRequest(tinaHitlSource, moodEventId);
    try {
      await resolveHitl(request.eventId, request.values, {
        navigateOnSuccess: false,
        notifyOnSuccess: false,
      });
    } catch (error) {
      Sentry.captureException(error);
      open?.({
        type: "error",
        message: "Event created, but Tina HITL was not closed",
        description: toHumanErrorMessage(error),
      });
    } finally {
      clearTinaHitlSource();
    }
  }

  const onInit = async (id: string, clone: boolean) => {
    Sentry.addBreadcrumb({
      category: "events.create",
      message: clone ? "Cloning event draft" : "Loading event draft",
      level: "info",
      data: { id },
    });
    setLoading(true);
    getDraft(id)
      .then((event) => {
        if (!clone) {
          setId(id);
        }
        if (event.startDate) {
          event.startDate = convertToTimezone(
            moment(event.startDate).toDate(),
            event.timezone,
          ).toISOString();
        }
        if (event.endDate) {
          event.endDate = convertToTimezone(
            moment(event.endDate).toDate(),
            event.timezone,
          ).toISOString();
        }
        updateBasicInfo(event as EventBasicInfoType);
        updateTicketing(event as EventTicketingType);
        if (event.media) {
          updateMedia(event as EventMediaType);
        }
      })
      .catch((e) => {
        open?.({
          type: "error",
          message: "Failed to load event draft",
          description: e?.response?.data?.detail || e.message,
        });
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const onBasicInfoStepSubmit = async (data: EventBasicInfoType) => {
    Sentry.addBreadcrumb({
      category: "events.create",
      message: "Submitting basic info step",
      level: "info",
      data: { name: data.name },
    });
    updateBasicInfo(data);
    updateState({ basicInfo: "loading" });
    try {
      const draftId = await draft({
        ...data,
        id,
        startDate: replaceTimezone(
          moment(data.startDate).toDate(),
          data.timezone,
        ).toISOString(),
        endDate: data.endDate
          ? replaceTimezone(
              moment(data.endDate).toDate(),
              data.timezone,
            ).toISOString()
          : data.endDate,
      });
      setId(draftId);
      setSearchParams({ id: draftId });
      updateState({ basicInfo: "success" });
      Sentry.addBreadcrumb({
        category: "events.create",
        message: "Basic info step complete — moving to ticketing",
        level: "info",
        data: { id: draftId },
      });
      methods.next();
    } catch (e) {
      Sentry.addBreadcrumb({
        category: "events.create",
        message: "Basic info step failed",
        level: "error",
      });
      updateState({ basicInfo: "error" });
    }
  };

  const onTicketingStepSubmit = async (data: EventTicketingType) => {
    Sentry.addBreadcrumb({
      category: "events.create",
      message: "Ticketing step complete — moving to media",
      level: "info",
    });
    updateTicketing(data);
    methods.next();
  };

  const onMediaStepSubmit = async (data: EventMediaType) => {
    Sentry.addBreadcrumb({
      category: "events.create",
      message: "Submitting media step — finalising event",
      level: "info",
    });
    updateMedia(data);
    updateState({ media: "loading" });
    let response: string;
    try {
      response = await complete({
        ...basicInfoStep,
        ...ticketingStep,
        ...data,
        id,
      } as EventDraftType);
      updateState({ media: "success" });
    } catch (error) {
      updateState({ media: "error" });
      open?.({
        type: "error",
        message: "Failed to create event",
        description: toHumanErrorMessage(error),
      });
      throw error;
    }

    await closeTinaHitlForCreatedEvent(response);
    setPendingUpdate({ type: "event_added_v2" });
    openConfirmationModal();
    // `?created=1` — see `enrichment-async-hint.tsx`. A Tina-deferred create
    // returns before its enrichment job has run.
    editCreated("events", response);
  };

  return {
    methods,
    setId,
    updateBasicInfo,
    updateTicketing,
    updateMedia,
    updateTinaHitlSource,
    updateState,
    onInit,
    onBasicInfoStepSubmit,
    onTicketingStepSubmit,
    onMediaStepSubmit,
    id,
    basicInfoStep,
    ticketingStep,
    mediaStep,
    loading,
  };
};
